import type { ExpressionNodeFe } from "@intelligems/ig-types";
import {
  AnalyticsAudienceType,
  AudienceDto,
  AudienceExpressionPayload,
  CheckoutBlockDto,
  CreateAudienceDto,
  ExperienceTestTypeDto,
  ExpressionNodeQueryType,
  ExpressionNodeType,
  GenericExperienceFe,
  OfferDto,
  OnsiteEditDto,
  OnsiteInjectionDto,
  RedirectDto,
  UrlMatch,
} from "@intelligems/ig-types";
import {
  expressionListToPostFix,
  postfixToExpressionList,
} from "@intelligems/ig-utils";
import {
  initialAudienceFiltersRolloutsGenerate,
  initialAudienceState,
} from "@src/api/initials";
import { getSpecificRedirectsUtil } from "@src/features/experiments/components/redirects/getSpecificRedirects";
import { isAudienceTargetingEnabled } from "@src/features/experiments/utils/validators";
import {
  RolloutAudienceSources,
  RolloutsInfoType,
} from "@src/store/slices/audiences/types";
import { IExperienceDashboardFilters } from "@src/store/types/dashboardFilters";

export const modLabelKey: { [key: string]: string } = {
  hasTestContentTheme: "Theme Change",
  hasTestContentTemplate: "Template Change",
  hasTestContentOnsite: "Onsite Edits",
  hasTestContentUrl: "URL Redirect",
  hasTestPricing: "Price Modification",
  hasTestCampaign: "Offer Modification",
  hasTestOnsiteInjections: "Custom CSS & JS",
  hasTestOfferTiered: "Volume Discount Offer",
  hasTestOfferPromotion: "Promotion Offer",
  hasTestOfferFreeGift: "Free Gift Offer",
  hasTestOfferFreeShipping: "Free Shipping Offer",
  hasTestOfferBuyXGetY: "Buy X Get Y Offer",
  hasTestCheckoutBlock: "Checkout Modification",
};

export const getNonNativeModsRollout = (
  experienceTestTypes: ExperienceTestTypeDto,
  includeTheme?: boolean
) => {
  return Object.keys(
    Object.fromEntries(
      Object.entries(experienceTestTypes).filter(([_, val]) => val)
    )
  ).filter(
    (val) =>
      val !== "hasTestContent" &&
      val !== "hasTestContentAdvanced" &&
      (includeTheme || val !== "hasTestContentTheme")
  );
};

export const getNonNativeOfferTestModsRollout = (
  redirects: RedirectDto[],
  onsiteEdits: OnsiteEditDto[],
  onsiteInjections: OnsiteInjectionDto[],
  offers: OfferDto[],
  variationId: string,
  checkoutBlocks: CheckoutBlockDto[],
  includeTheme?: boolean
) => {
  const modsToReturn: string[] = [];
  const templateRedirects = getSpecificRedirectsUtil(
    redirects,
    "template"
  ).filter((redirect) => redirect.variationId === variationId);
  if (templateRedirects.length) {
    modsToReturn.push("hasTestContentTemplate");
  }
  const urlRedirects = getSpecificRedirectsUtil(redirects, "url").filter(
    (redirect) => redirect.variationId === variationId
  );
  if (urlRedirects.length) {
    modsToReturn.push("hasTestContentUrl");
  }

  if (includeTheme) {
    const themeRedirects = getSpecificRedirectsUtil(redirects, "theme").filter(
      (redirect) => redirect.variationId === variationId
    );
    if (themeRedirects.length) {
      modsToReturn.push("hasTestContentTheme");
    }
  }

  const varOffer = offers.find((offer) => offer.variationId === variationId);
  if (varOffer) {
    modsToReturn.push(
      varOffer.discountApplicationType === "tieredDiscount"
        ? "hasTestOfferTiered"
        : varOffer.discountApplicationType === "cartDiscount"
          ? "hasTestOfferPromotion"
          : varOffer.discountApplicationType === "freeShipping"
            ? "hasTestOfferFreeShipping"
            : varOffer.discountApplicationType === "buyXGetY"
              ? "hasTestOfferBuyXGetY"
              : "hasTestOfferFreeGift"
    );
  }

  const varOnsiteInjections = (onsiteInjections || [])?.find(
    (injection) => injection.variationId === variationId
  );

  if (
    varOnsiteInjections &&
    (varOnsiteInjections.customCss || varOnsiteInjections.customJs)
  ) {
    modsToReturn.push("hasTestOnsiteInjections");
  }

  if (onsiteEdits.length) {
    modsToReturn.push("hasTestContentOnsite");
  }
  if (checkoutBlocks.length) {
    modsToReturn.push("hasTestCheckoutBlock");
  }
  return modsToReturn;
};

export const convertDashboardFilterToAudience = (
  dashboardFilters: IExperienceDashboardFilters
): AudienceExpressionPayload[] => {
  const expressionsList: AudienceExpressionPayload[] = [];
  if (dashboardFilters.countryCodes && dashboardFilters.countryCodes.length) {
    dashboardFilters.countryCodes
      .filter((country) => country !== null)
      .forEach((country) => {
        expressionsList.push({
          type: "country",
          queries: [
            {
              type: "country",
              filter: "equals",
              key: "country",
              value: country,
            },
          ],
        });
      });
  }
  if (dashboardFilters.deviceType && dashboardFilters.deviceType !== "any") {
    expressionsList.push({
      type: "device",
      queries: [
        {
          type: "device",
          filter: "equals",
          key: "device",
          value: dashboardFilters.deviceType,
        },
      ],
    });
  }
  if (dashboardFilters.visitorType && dashboardFilters.visitorType !== "any") {
    expressionsList.push({
      type: "visitor",
      queries: [
        {
          type: "visitor",
          filter: "equals",
          key: "visitor",
          value: dashboardFilters.visitorType,
        },
      ],
    });
  }

  if (
    dashboardFilters.urlParam &&
    dashboardFilters.urlParam.queryParam !== "" &&
    dashboardFilters.urlParam.queryParam &&
    dashboardFilters.urlParam.queryParamValue
  ) {
    expressionsList.push({
      type: "utm",
      queries: [
        {
          type: "utm",
          filter: dashboardFilters.urlParam.queryParamFilterType as UrlMatch,
          key: dashboardFilters.urlParam.queryParam,
          value: dashboardFilters.urlParam.queryParamValue,
        },
      ],
    });
  }

  if (
    dashboardFilters.landingPage &&
    dashboardFilters.landingPage.landingPageUrlPathFilterType
  ) {
    const landingPage = dashboardFilters.landingPage;
    const isNull = ["isNull", "isNotNull"].includes(
      landingPage.landingPageUrlPathFilterType || ""
    );
    if (landingPage.landingPageUrlPath || isNull) {
      expressionsList.push({
        type: "landingPage",
        queries: [
          {
            type: "landingPage",
            filter: landingPage.landingPageUrlPathFilterType,
            key: "landingPage",
            value: landingPage.landingPageUrlPath || "",
          },
        ],
      });
    }
  }

  // No "any" guard: channels is an analytics filter, and the analytics channel
  // list has no "any" sentinel — an empty array already means "all channels".
  if (dashboardFilters.channels && dashboardFilters.channels.length > 0) {
    dashboardFilters.channels.forEach((channel) => {
      expressionsList.push({
        type: "trafficSource",
        queries: [
          {
            type: "trafficSource",
            filter: "equals",
            key: "trafficSource",
            value: channel,
          },
        ],
      });
    });
  }
  return expressionsList;
};

export const combineAllAudienceSources = ({
  dashboardFilters,
  sourcesToBeUsed,
  experienceAudience,
  tableSelection,
}: {
  dashboardFilters?: AudienceExpressionPayload[];
  sourcesToBeUsed: RolloutAudienceSources;
  experienceAudience?: AudienceDto | CreateAudienceDto;
  tableSelection?: ExpressionNodeFe<ExpressionNodeQueryType>[];
}): {
  generatedAudience: CreateAudienceDto;
  sourcesUsed: RolloutAudienceSources;
} => {
  const expressionsList: AudienceExpressionPayload[] = [];
  const sourcesUsed: RolloutAudienceSources = {
    testTargeting: false,
    dashboardTargeting: false,
    analyticTableTargeting: false,
  };
  if (
    experienceAudience &&
    experienceAudience.audienceType !== "advanced" &&
    experienceAudience.filters.length &&
    sourcesToBeUsed.testTargeting
  ) {
    sourcesUsed.testTargeting = true;
    expressionsList.push(
      ...postfixToExpressionList(experienceAudience.filters[0].expression!)
    );
  }

  if (
    sourcesToBeUsed.dashboardTargeting &&
    dashboardFilters &&
    dashboardFilters.length
  ) {
    sourcesUsed.dashboardTargeting = true;
    expressionsList.push(...dashboardFilters);
  }

  if (
    tableSelection &&
    tableSelection.length &&
    sourcesToBeUsed.analyticTableTargeting
  ) {
    sourcesUsed.analyticTableTargeting = true;
    expressionsList.push({
      type: tableSelection[0].query!.type,
      queries: [
        {
          type: tableSelection[0].query!.type,
          filter: "equals",
          key: tableSelection[0].query!.key,
          value: tableSelection[0].query!.value,
        },
      ],
    });
  }
  const initialAudienceFilters: CreateAudienceDto = {
    ...initialAudienceState(),
    audienceType: "custom",
    ...(expressionsList.length && {
      filters: [
        initialAudienceFiltersRolloutsGenerate(
          expressionListToPostFix(expressionsList)
        ),
      ],
    }),
  };

  return { generatedAudience: initialAudienceFilters, sourcesUsed };
};

export const getIsAllVisitors = (
  filterTargeting: ExpressionNodeType[],
  tableTargeting: ExpressionNodeType[],
  storedAudience: AudienceDto | CreateAudienceDto | undefined | null
) => {
  const testTargetingEnabled = Boolean(
    storedAudience && storedAudience?.filters?.length > 0
  );

  if (
    !testTargetingEnabled &&
    (!filterTargeting || filterTargeting?.length === 0) &&
    (!tableTargeting || tableTargeting?.length === 0)
  ) {
    return "all";
  } else {
    return "limited";
  }
};

export const hasOnlyNativeMods = (testTypes: ExperienceTestTypeDto) => {
  return (
    testTypes.hasTestContentTheme &&
    !testTypes.hasTestContentOnsite &&
    !testTypes.hasTestOnsiteInjections &&
    !testTypes.hasTestContentUrl &&
    !testTypes.hasTestContentTemplate
  );
};

export const hasNativeAndNonNativeMods = (testTypes: ExperienceTestTypeDto) => {
  return (
    testTypes.hasTestContentTheme &&
    (testTypes.hasTestContentOnsite ||
      testTypes.hasTestOnsiteInjections ||
      testTypes.hasTestContentUrl ||
      testTypes.hasTestContentTemplate)
  );
};

export const getRolloutType = (
  isAllVisitors: string,
  experience: GenericExperienceFe
) => {
  return isAllVisitors !== "all"
    ? "allMods"
    : hasOnlyNativeMods(experience.testTypes)
      ? "noMods"
      : "nonNativeModsOnly";
};

export const formatOverviewTableFilter = (
  data: any,
  currAudienceSegment: AnalyticsAudienceType | undefined
): ExpressionNodeType[] => {
  if (!currAudienceSegment) {
    return [];
  }
  switch (currAudienceSegment) {
    case "country_code":
      return [
        {
          query: {
            type: "country",
            value: data.audience,
            filter: "equals",
            key: "country",
          },
        },
      ];
    case "device_type":
      return [
        {
          query: {
            type: "device",
            value: data.audience.toLowerCase(),
            filter: "equals",
            key: "device",
          },
        },
      ];
    case "landing_page_full_path": // needs to be custom
      return [
        {
          query: {
            type: "landingPage",
            value: data.audience,
            filter: "equals",
            key: "landingPage",
          },
        },
      ];
    case "visitor_type":
      return [
        {
          query: {
            type: "visitor",
            value: data.audience.toLowerCase(),
            filter: "equals",
            key: "visitor",
          },
        },
      ];
    case "source_channel":
    case "source_site":
      return [
        {
          query: {
            type: "trafficSource",
            value: data.audience,
            filter: "equals",
            key: "trafficSource",
          },
        },
      ];
    case "purchase_likelihood":
      return [
        {
          query: {
            type: "purchaseLikelihood",
            value: data.audience,
            filter: "equals",
            key: "purchaseLikelihood",
          },
        },
      ];
    default:
      return [];
  }
};

export const getHideSummary = (
  isAllVisitors: string,
  storedAudienceState: AudienceDto | CreateAudienceDto | undefined,
  rolloutsInfo: RolloutsInfoType | undefined
) => {
  if (!rolloutsInfo) {
    return true;
  }
  const hasDashboardTargeting = rolloutsInfo.dashboardTargeting.length;
  const hasTableTargeting = rolloutsInfo.analyticTableTargeting.length;
  const hasTestTargeting =
    storedAudienceState && isAudienceTargetingEnabled(storedAudienceState);
  return (
    (!hasTestTargeting && !hasTableTargeting && !hasDashboardTargeting) ||
    isAllVisitors !== "limited"
  );
};

export const generateRolloutChangesCopy = (
  audience: AudienceDto,
  pageTargetingLength: number,
  isLinkTargeting: boolean
) => {
  const shouldShowAudienceCopy = audience && audience.filters.length;
  const shouldShowPageTargetingCopy = pageTargetingLength > 0;
  const shouldShowCurrencyCopy = audience?.excludeCurrency?.currency?.length;

  if (
    !shouldShowAudienceCopy &&
    !shouldShowPageTargetingCopy &&
    !shouldShowCurrencyCopy &&
    !isLinkTargeting
  ) {
    return "Changes will be visible to all visitors";
  }

  let copyToRender = "Changes will be visible to visitors";

  if (shouldShowAudienceCopy) {
    copyToRender += " matching the audience";
  }

  if (shouldShowPageTargetingCopy) {
    copyToRender += shouldShowAudienceCopy ? " and" : " matching the";
    copyToRender += " page targeting";
  }

  if (shouldShowAudienceCopy || shouldShowPageTargetingCopy) {
    copyToRender += " conditions";
  }

  if (isLinkTargeting) {
    copyToRender += " with the specific link";
  }

  if (shouldShowCurrencyCopy) {
    if (shouldShowAudienceCopy || shouldShowPageTargetingCopy) {
      copyToRender += ", and is";
    }
    const currencyToRender = audience.excludeCurrency.currency
      ?.split(",")
      .join(", ");
    const isMultipleCurrencies =
      audience.excludeCurrency.currency?.length &&
      audience.excludeCurrency.currency?.split(",").length > 1;
    copyToRender += ` in
                ${currencyToRender}
                ${isMultipleCurrencies ? "currencies" : "currency"}`;
  }

  return copyToRender;
};
